/**
 * @fileoverview Product Card Image and Color Swatch Management
 * @description Handles product card image generation and color swatch functionality
 *
 * Features:
 * - Responsive image generation with srcset
 * - Dynamic image swapping for color variants
 * - Color swatch interaction handling
 * - Smooth image transition animations
 *
 * HTML Structure Required:
 * - Elements with class "js-color-swatches-wrapper" for swatch containers
 * - Elements with class "js-color-swatches" containing color swatch inputs
 * - Elements with class "media--first" for primary product images
 * - Elements with class "media--second" for secondary product images
 */

/**
 * @function generateSrcset
 * @description Generates a srcset string for responsive images
 * @param {Object} image - The image object containing src and width
 * @param {number[]} widths - Array of desired image widths
 * @returns {string} The generated srcset string
 */
const generateSrcset = (image, widths = []) => {
  const imageUrl = new URL(image['src']);
  return widths
    .filter((width) => width <= image['width'])
    .map((width) => {
      imageUrl.searchParams.set('width', width.toString());
      return `${imageUrl.href} ${width}w`;
    })
    .join(', ');
};

/**
 * @function createImageElement
 * @description Creates a new image element with responsive attributes
 * @param {Object} image - The image object containing preview_image
 * @param {string} classes - CSS classes to apply to the image
 * @param {string} sizes - The sizes attribute value
 * @param {string} productTitle - The product title for alt text
 * @returns {HTMLImageElement} The created image element
 */
const createImageElement = (image, classes, sizes, productTitle) => {
  const previewImage = image['preview_image'];
  const newImage = new Image(previewImage['width'], previewImage['height']);
  newImage.className = classes;
  newImage.alt = image['alt'] || productTitle;
  newImage.sizes = sizes;
  newImage.src = previewImage['src'];
  newImage.srcset = generateSrcset(
    previewImage,
    [165, 360, 533, 720, 940, 1066]
  );
  newImage.loading = 'lazy';
  return newImage;
};

/**
 * @function checkSwatches
 * @description Sets up event listeners for color swatch interactions
 * - Handles color variant selection
 * - Updates product images based on selected color
 * - Manages image transitions
 */
const checkSwatches = () => {
  document.querySelectorAll('.js-color-swatches-wrapper').forEach((wrapper) => {
    wrapper.querySelectorAll('.js-color-swatches input').forEach((input) => {
      input.addEventListener('click', (event) => {
        const primaryImage = wrapper.querySelector('.media--first');
        const secondaryImage = wrapper.querySelector('.media--second');
        const handleProduct = wrapper.dataset.product;

        if (event.currentTarget.checked && primaryImage) {
          wrapper
            .querySelectorAll('.js-color-swatches-link')
            .forEach((item) =>
              item.setAttribute('href', event.currentTarget.dataset.variantLink)
            );
          const currentColor = event.currentTarget.value;

          jQuery.getJSON(
            window.Shopify.routes.root + `products/${handleProduct}.js`,
            function (product) {
              const variant = product.variants.filter(
                (item) =>
                  item.featured_media != null &&
                  item.options.includes(currentColor)
              )[0];
              if (variant) {
                const newPrimaryImage = createImageElement(
                  variant['featured_media'],
                  primaryImage.className,
                  primaryImage.sizes,
                  product.title
                );

                if (newPrimaryImage.src !== primaryImage.src) {
                  let flag = false;
                  if (secondaryImage) {
                    const secondaryImagePathname = new URL(secondaryImage.src)
                      .pathname;
                    const newPrimaryImagePathname = new URL(newPrimaryImage.src)
                      .pathname;

                    if (secondaryImagePathname == newPrimaryImagePathname) {
                      primaryImage.remove();
                      secondaryImage.classList.remove('media--second');
                      secondaryImage.classList.add('media--first');
                      flag = true;
                    }
                  }
                  if (flag == false) {
                    primaryImage.animate(
                      { opacity: [1, 0] },
                      { duration: 200, easing: 'ease-in', fill: 'forwards' }
                    ).finished;
                    setTimeout(function () {
                      primaryImage.replaceWith(newPrimaryImage);
                      newPrimaryImage.animate(
                        { opacity: [0, 1] },
                        { duration: 200, easing: 'ease-in' }
                      );
                      if (secondaryImage) {
                        secondaryImage.remove();
                      }
                    }, 200);
                  }
                }
              }
            }
          );
        }
      });
    });
  });
};

/**
 * @function colorSwatches
 * @description Initializes color swatch functionality
 * - Sets up initial swatch listeners
 * - Handles Shopify section load events
 */
function colorSwatches() {
  checkSwatches();

  document.addEventListener('shopify:section:load', function () {
    checkSwatches();
  });
}

// Initialize color swatches on page load
(function () {
  colorSwatches();
})();
